import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Search, Plus, Eye, Pencil, Trash2, Loader2, Users, Copy, MessageCircle, CheckCircle, Camera, UserCircle } from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  aktif: 'bg-green-100 text-green-700',
  'non-aktif': 'bg-gray-100 text-gray-700',
  cuti: 'bg-yellow-100 text-yellow-700',
  resign: 'bg-red-100 text-red-700',
};

const KontrakBadge = ({ sisaHari, tanggalBerakhir }) => {
  if (!tanggalBerakhir) return <Badge className="bg-gray-100 text-gray-500" data-testid="kontrak-badge">-</Badge>;
  let color = 'bg-green-100 text-green-700';
  let label = `${sisaHari} hari`;
  if (sisaHari < 0) {
    color = 'bg-red-100 text-red-700';
    label = `Lewat ${Math.abs(sisaHari)} hari`;
  } else if (sisaHari <= 30) {
    color = 'bg-amber-100 text-amber-700';
  }
  return <Badge className={color} data-testid="kontrak-badge">{label}</Badge>;
};

const EmployeesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isHR } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('all');
  const [filterSite, setFilterSite] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [newCredentials, setNewCredentials] = useState(null);
  const [copied, setCopied] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    nik: '',
    nama_lengkap: '',
    email: '',
    telepon: '',
    no_wa: '',
    alamat: '',
    tempat_lahir: '',
    tanggal_lahir: '',
    jenis_kelamin: '',
    agama: '',
    pendidikan: '',
    status_keluarga: '',
    no_ktp: '',
    npwp: '',
    bank: '',
    no_rekening: '',
    tanggal_bergabung: '',
    department_id: '',
    position_id: '',
    site_id: '',
    status: 'aktif',
    foto_url: '',
    tanggal_mulai_kontrak: '',
    tanggal_perpanjangan_kontrak: '',
    tanggal_berakhir_kontrak: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const pendingEditId = location.state?.editEmployeeId;
    if (pendingEditId && employees.length > 0) {
      const emp = employees.find((e) => e.id === pendingEditId);
      if (emp) openEditDialog(emp);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [employees]);

  const fetchData = async () => {
    try {
      const [empRes, deptRes, posRes, siteRes] = await Promise.all([
        api.get('/employees'),
        api.get('/departments'),
        api.get('/positions'),
        api.get('/sites'),
      ]);
      setEmployees(empRes.data);
      setDepartments(deptRes.data);
      setPositions(posRes.data);
      setSites(siteRes.data);
    } catch (error) {
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = employees.filter((emp) => {
    const matchSearch =
      emp.nama_lengkap.toLowerCase().includes(search.toLowerCase()) ||
      emp.nik.toLowerCase().includes(search.toLowerCase()) ||
      emp.email.toLowerCase().includes(search.toLowerCase());
    const matchDept = filterDept === 'all' || emp.department_id === filterDept;
    const matchSite = filterSite === 'all' || emp.site_id === filterSite;
    const matchStatus = filterStatus === 'all' || emp.status === filterStatus;
    return matchSearch && matchDept && matchSite && matchStatus;
  });

  const handleFotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Ukuran foto terlalu besar (maks 10MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX_PX = 800;
        let w = img.width, h = img.height;
        if (Math.max(w, h) > MAX_PX) {
          const r = MAX_PX / Math.max(w, h);
          w = Math.round(w * r); h = Math.round(h * r);
        }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        const compressed = canvas.toDataURL('image/jpeg', 0.75);
        setPhotoPreview(compressed);
        setFormData(prev => ({ ...prev, foto_url: compressed }));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (selectedEmployee) {
        await api.put(`/employees/${selectedEmployee.id}`, formData);
        toast.success('Karyawan berhasil diperbarui');
        setShowAddDialog(false);
        resetForm();
        fetchData();
      } else {
        // Create new employee - will return credentials
        const response = await api.post('/employees', formData);
        setShowAddDialog(false);
        resetForm();
        
        // Show credentials modal
        if (response.data.credentials) {
          setNewCredentials({
            nama: response.data.employee.nama_lengkap,
            telepon: response.data.employee.telepon,
            password: response.data.credentials.password,
            login_url: response.data.credentials.login_url
          });
          setShowCredentialsDialog(true);
        }
        
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan data');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEmployee) return;
    setSubmitting(true);

    try {
      await api.delete(`/employees/${selectedEmployee.id}`);
      toast.success('Karyawan berhasil dihapus');
      setShowDeleteDialog(false);
      setSelectedEmployee(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus data');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (emp) => {
    setSelectedEmployee(emp);
    setFormData({
      nik: emp.nik,
      nama_lengkap: emp.nama_lengkap,
      email: emp.email,
      telepon: emp.telepon || '',
      no_wa: emp.no_wa || '',
      alamat: emp.alamat || '',
      tempat_lahir: emp.tempat_lahir || '',
      tanggal_lahir: emp.tanggal_lahir || '',
      jenis_kelamin: emp.jenis_kelamin || '',
      agama: emp.agama || '',
      pendidikan: emp.pendidikan || '',
      status_keluarga: emp.status_keluarga || '',
      no_ktp: emp.no_ktp || '',
      npwp: emp.npwp || '',
      bank: emp.bank || '',
      no_rekening: emp.no_rekening || '',
      tanggal_bergabung: emp.tanggal_bergabung || '',
      department_id: emp.department_id || '',
      position_id: emp.position_id || '',
      site_id: emp.site_id || '',
      status: emp.status || 'aktif',
      foto_url: emp.foto_url || '',
      tanggal_mulai_kontrak: emp.tanggal_mulai_kontrak || '',
      tanggal_perpanjangan_kontrak: emp.tanggal_perpanjangan_kontrak || '',
      tanggal_berakhir_kontrak: emp.tanggal_berakhir_kontrak || '',
    });
    setPhotoPreview(emp.foto_url || null);
    setShowAddDialog(true);
  };

  const openDeleteDialog = (emp) => {
    setSelectedEmployee(emp);
    setShowDeleteDialog(true);
  };

  const resetForm = () => {
    setSelectedEmployee(null);
    setFormData({
      nik: '',
      nama_lengkap: '',
      email: '',
      telepon: '',
      alamat: '',
      tempat_lahir: '',
      tanggal_lahir: '',
      jenis_kelamin: '',
      agama: '',
      pendidikan: '',
      status_keluarga: '',
      no_ktp: '',
      npwp: '',
      bank: '',
      no_rekening: '',
      tanggal_bergabung: '',
      department_id: '',
      position_id: '',
      site_id: '',
      status: 'aktif',
      foto_url: '',
      tanggal_mulai_kontrak: '',
      tanggal_perpanjangan_kontrak: '',
      tanggal_berakhir_kontrak: '',
    });
  };

  const handleCopyCredentials = () => {
    if (!newCredentials) return;
    
    const text = `Halo ${newCredentials.nama}!\n\nAkun SILSA Anda sudah dibuat.\n\n📱 No. HP Login: ${newCredentials.telepon}\n🔑 Password: ${newCredentials.password}\n🔗 Login: ${newCredentials.login_url}\n\nAnda bisa login lalu pilih untuk tetap pakai password ini atau menggantinya.`;
    
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success('Berhasil disalin!');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleShareWhatsApp = () => {
    if (!newCredentials) return;
    
    const text = `Halo ${newCredentials.nama}!\n\nAkun SILSA Anda sudah dibuat.\n\n📱 No. HP Login: ${newCredentials.telepon}\n🔑 Password: ${newCredentials.password}\n🔗 Login: ${newCredentials.login_url}\n\nAnda bisa login lalu pilih untuk tetap pakai password ini atau menggantinya.`;
    
    const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(waUrl, '_blank');
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground" data-testid="page-title">Karyawan</h1>
          <p className="text-muted-foreground">Kelola data karyawan perusahaan</p>
        </div>
        {isHR && (
          <Button onClick={() => { resetForm(); setShowAddDialog(true); }} data-testid="add-employee-btn">
            <Plus className="w-4 h-4 mr-2" />
            Tambah Karyawan
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Cari nama, NIK, atau email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={filterDept} onValueChange={setFilterDept}>
              <SelectTrigger className="w-[200px]" data-testid="filter-dept">
                <SelectValue placeholder="Departemen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Departemen</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept.id} value={dept.id}>
                    {dept.nama}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterSite} onValueChange={setFilterSite}>
              <SelectTrigger className="w-[180px]" data-testid="filter-site">
                <SelectValue placeholder="Site" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Site</SelectItem>
                {sites.map((site) => (
                  <SelectItem key={site.id} value={site.id}>
                    {site.nama}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                <SelectItem value="aktif">Aktif</SelectItem>
                <SelectItem value="non-aktif">Non-Aktif</SelectItem>
                <SelectItem value="cuti">Cuti</SelectItem>
                <SelectItem value="resign">Resign</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Employees Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Daftar Karyawan ({filteredEmployees.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Tidak ada karyawan ditemukan</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Karyawan</TableHead>
                  <TableHead>NIK</TableHead>
                  <TableHead>Departemen</TableHead>
                  <TableHead>Posisi</TableHead>
                  <TableHead>Site</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Kontrak</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.map((emp) => (
                  <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={emp.foto_url} />
                          <AvatarFallback className="bg-primary/10 text-primary">
                            {getInitials(emp.nama_lengkap)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{emp.nama_lengkap}</p>
                          <p className="text-sm text-muted-foreground">{emp.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono">{emp.nik}</TableCell>
                    <TableCell>{emp.department_nama}</TableCell>
                    <TableCell>{emp.position_nama}</TableCell>
                    <TableCell>{emp.site_nama || '-'}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[emp.status] || 'bg-gray-100'}>
                        {emp.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <KontrakBadge sisaHari={emp.sisa_hari_kontrak} tanggalBerakhir={emp.tanggal_berakhir_kontrak} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/employees/${emp.id}`)}
                          data-testid={`view-btn-${emp.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {isHR && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(emp)}
                              data-testid={`edit-btn-${emp.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openDeleteDialog(emp)}
                              data-testid={`delete-btn-${emp.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedEmployee ? 'Edit Karyawan' : 'Tambah Karyawan Baru'}</DialogTitle>
            <DialogDescription>
              {selectedEmployee ? 'Perbarui data karyawan' : 'Isi data karyawan baru. Akun login akan dibuat otomatis.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 py-4">
              {/* Foto Profil */}
              <div className="col-span-2 flex flex-col items-center gap-3">
                <div
                  className="w-24 h-24 rounded-full border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden cursor-pointer hover:border-primary transition-colors bg-slate-50"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="foto-upload-area"
                >
                  {photoPreview ? (
                    <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex flex-col items-center gap-1 text-slate-400">
                      <UserCircle className="w-10 h-10" />
                      <span className="text-xs">Foto</span>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                  data-testid="foto-upload-btn"
                >
                  <Camera className="w-4 h-4" />
                  {photoPreview ? 'Ganti Foto' : 'Upload Foto Profil'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFotoSelect}
                  data-testid="foto-file-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nik">NIK *</Label>
                <Input
                  id="nik"
                  value={formData.nik}
                  onChange={(e) => setFormData({ ...formData, nik: e.target.value })}
                  placeholder="EMP001"
                  required
                  data-testid="input-nik"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nama_lengkap">Nama Lengkap *</Label>
                <Input
                  id="nama_lengkap"
                  value={formData.nama_lengkap}
                  onChange={(e) => setFormData({ ...formData, nama_lengkap: e.target.value })}
                  placeholder="John Doe"
                  required
                  data-testid="input-nama"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@company.com"
                  required
                  data-testid="input-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telepon">Telepon</Label>
                <Input
                  id="telepon"
                  value={formData.telepon}
                  onChange={(e) => setFormData({ ...formData, telepon: e.target.value })}
                  placeholder="08123456789"
                  data-testid="input-telepon"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="no_wa">No. WhatsApp</Label>
                <Input
                  id="no_wa"
                  value={formData.no_wa}
                  onChange={(e) => setFormData({ ...formData, no_wa: e.target.value })}
                  placeholder="08123456789"
                  data-testid="input-no-wa"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label htmlFor="alamat">Alamat</Label>
                <Input
                  id="alamat"
                  value={formData.alamat}
                  onChange={(e) => setFormData({ ...formData, alamat: e.target.value })}
                  placeholder="Jl. Contoh No. 123"
                  data-testid="input-alamat"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tempat_lahir">Tempat Lahir</Label>
                <Input
                  id="tempat_lahir"
                  value={formData.tempat_lahir}
                  onChange={(e) => setFormData({ ...formData, tempat_lahir: e.target.value })}
                  placeholder="Jakarta"
                  data-testid="input-tempat-lahir"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tanggal_lahir">Tanggal Lahir</Label>
                <Input
                  id="tanggal_lahir"
                  type="date"
                  value={formData.tanggal_lahir}
                  onChange={(e) => setFormData({ ...formData, tanggal_lahir: e.target.value })}
                  data-testid="input-tgl-lahir"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="jenis_kelamin">Jenis Kelamin</Label>
                <Select
                  value={formData.jenis_kelamin}
                  onValueChange={(value) => setFormData({ ...formData, jenis_kelamin: value })}
                >
                  <SelectTrigger data-testid="select-gender">
                    <SelectValue placeholder="Pilih" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Laki-laki">Laki-laki</SelectItem>
                    <SelectItem value="Perempuan">Perempuan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tanggal_bergabung">Tanggal Bergabung *</Label>
                <Input
                  id="tanggal_bergabung"
                  type="date"
                  value={formData.tanggal_bergabung}
                  onChange={(e) => setFormData({ ...formData, tanggal_bergabung: e.target.value })}
                  required
                  data-testid="input-tgl-gabung"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="agama">Agama</Label>
                <Input
                  id="agama"
                  value={formData.agama}
                  onChange={(e) => setFormData({ ...formData, agama: e.target.value })}
                  placeholder="Islam"
                  data-testid="input-agama"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pendidikan">Pendidikan</Label>
                <Input
                  id="pendidikan"
                  value={formData.pendidikan}
                  onChange={(e) => setFormData({ ...formData, pendidikan: e.target.value })}
                  placeholder="S1, SMA, dll"
                  data-testid="input-pendidikan"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status_keluarga">Status Keluarga</Label>
                <Input
                  id="status_keluarga"
                  value={formData.status_keluarga}
                  onChange={(e) => setFormData({ ...formData, status_keluarga: e.target.value })}
                  placeholder="TK/K0/K1/K2"
                  data-testid="input-status-keluarga"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="no_ktp">No. KTP</Label>
                <Input
                  id="no_ktp"
                  value={formData.no_ktp}
                  onChange={(e) => setFormData({ ...formData, no_ktp: e.target.value })}
                  placeholder="16 digit NIK KTP"
                  data-testid="input-ktp"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="npwp">NPWP</Label>
                <Input
                  id="npwp"
                  value={formData.npwp}
                  onChange={(e) => setFormData({ ...formData, npwp: e.target.value })}
                  data-testid="input-npwp"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bank">Bank</Label>
                <Input
                  id="bank"
                  value={formData.bank}
                  onChange={(e) => setFormData({ ...formData, bank: e.target.value })}
                  placeholder="MANDIRI, BCA, dll"
                  data-testid="input-bank"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="no_rekening">No. Rekening</Label>
                <Input
                  id="no_rekening"
                  value={formData.no_rekening}
                  onChange={(e) => setFormData({ ...formData, no_rekening: e.target.value })}
                  data-testid="input-rekening"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="department_id">Departemen *</Label>
                <Select
                  value={formData.department_id}
                  onValueChange={(value) => setFormData({ ...formData, department_id: value })}
                  required
                >
                  <SelectTrigger data-testid="select-dept">
                    <SelectValue placeholder="Pilih Departemen" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>
                        {dept.nama}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="position_id">Posisi *</Label>
                <Select
                  value={formData.position_id}
                  onValueChange={(value) => setFormData({ ...formData, position_id: value })}
                  required
                >
                  <SelectTrigger data-testid="select-position">
                    <SelectValue placeholder="Pilih Posisi" />
                  </SelectTrigger>
                  <SelectContent>
                    {positions.map((pos) => (
                      <SelectItem key={pos.id} value={pos.id}>
                        {pos.nama}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="site_id">Site (Lokasi Kerja)</Label>
                <Select
                  value={formData.site_id || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, site_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger data-testid="select-site">
                    <SelectValue placeholder="Pilih Site" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Tidak ada</SelectItem>
                    {sites.map((site) => (
                      <SelectItem key={site.id} value={site.id}>
                        {site.nama}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Menentukan lokasi (geofence) absen karyawan</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tanggal_mulai_kontrak">Tanggal Mulai Kontrak</Label>
                <Input
                  id="tanggal_mulai_kontrak"
                  type="date"
                  value={formData.tanggal_mulai_kontrak}
                  onChange={(e) => setFormData({ ...formData, tanggal_mulai_kontrak: e.target.value })}
                  data-testid="input-tgl-mulai-kontrak"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tanggal_perpanjangan_kontrak">Tanggal Perpanjangan Kontrak</Label>
                <Input
                  id="tanggal_perpanjangan_kontrak"
                  type="date"
                  value={formData.tanggal_perpanjangan_kontrak}
                  onChange={(e) => setFormData({ ...formData, tanggal_perpanjangan_kontrak: e.target.value })}
                  data-testid="input-tgl-perpanjangan-kontrak"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tanggal_berakhir_kontrak">Tanggal Berakhir Kontrak</Label>
                <Input
                  id="tanggal_berakhir_kontrak"
                  type="date"
                  value={formData.tanggal_berakhir_kontrak}
                  onChange={(e) => setFormData({ ...formData, tanggal_berakhir_kontrak: e.target.value })}
                  data-testid="input-tgl-berakhir-kontrak"
                />
                <p className="text-xs text-muted-foreground">Reminder otomatis muncul kalau tinggal &lt;30 hari</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="select-status">
                    <SelectValue placeholder="Pilih Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aktif">Aktif</SelectItem>
                    <SelectItem value="non-aktif">Non-Aktif</SelectItem>
                    <SelectItem value="cuti">Cuti</SelectItem>
                    <SelectItem value="resign">Resign</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {!selectedEmployee && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Info:</strong> Akun login akan dibuat otomatis dengan password yang di-generate. 
                  Anda akan mendapat info kredensial setelah karyawan berhasil ditambahkan.
                </p>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Batal
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {selectedEmployee ? 'Simpan Perubahan' : 'Tambah Karyawan'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credentials Modal */}
      <Dialog open={showCredentialsDialog} onOpenChange={setShowCredentialsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-6 h-6" />
              Karyawan Berhasil Ditambahkan!
            </DialogTitle>
            <DialogDescription>
              Berikut adalah kredensial login untuk karyawan baru. Kirim informasi ini kepada karyawan.
            </DialogDescription>
          </DialogHeader>
          
          {newCredentials && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div>
                  <Label className="text-xs text-slate-500">Nama</Label>
                  <p className="font-medium">{newCredentials.nama}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">No. HP Login</Label>
                  <p className="font-mono bg-white px-2 py-1 rounded border">{newCredentials.telepon}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Password</Label>
                  <p className="font-mono bg-white px-2 py-1 rounded border text-lg font-bold text-primary">{newCredentials.password}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Link Login</Label>
                  <p className="font-mono bg-white px-2 py-1 rounded border text-sm break-all">{newCredentials.login_url}</p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={handleCopyCredentials} 
                  variant="outline" 
                  className="flex-1"
                  data-testid="copy-credentials-btn"
                >
                  {copied ? <CheckCircle className="w-4 h-4 mr-2 text-green-500" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Tersalin!' : 'Salin'}
                </Button>
                <Button 
                  onClick={handleShareWhatsApp} 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="share-wa-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Kirim via WA
                </Button>
              </div>

              <p className="text-xs text-slate-500 text-center">
                Karyawan bisa login lalu memilih tetap pakai password ini atau menggantinya.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => { setShowCredentialsDialog(false); setNewCredentials(null); }}>
              Selesai
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Hapus Karyawan</DialogTitle>
            <DialogDescription>
              Apakah Anda yakin ingin menghapus karyawan{' '}
              <strong>{selectedEmployee?.nama_lengkap}</strong>? Tindakan ini tidak dapat dibatalkan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Batal
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={submitting} data-testid="confirm-delete-btn">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Hapus
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeesPage;
